import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { DeviceLicenceResponse, DeviceLicence } from '../models/device-licence';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {ScgHttpWrapper} from '@app/core/injectables/scg-http-wrapper';
import {Router} from '@angular/router';
import {Device} from '@app/core/models/device';

@Injectable({
  providedIn: 'root'
})
export class LicenceService {

  constructor(private http: HttpClient, private router: Router) { }

  /**
   * Gets the licence for the currently active device from the API
   */
  public getLicence(fastMode: boolean): Observable<DeviceLicence> {
    let url = 'licence/v2/licencecheck';
    if (fastMode) url = url + '?mode=fast';
    return this.http.get<DeviceLicence>(environment.apiUrl + url).pipe(
      map<DeviceLicenceResponse, DeviceLicence>(response => new DeviceLicence(response))
    );
  }

  /**
   * Gets the login email name for mydata display reasons.
   *
   * Also carries the identity of the current session: `loginEmail` is the address this session
   * actually logged in with, `isCoAccount` tells whether this is an additional login (CoAccount)
   * of another customer. `email` is always the main account, never the CoAccount.
   */
  public getLoginEmail(): Observable<LoginEmail>  {
    return this.http.get<LoginEmail>(environment.apiUrl + 'licence/listemail');
  }

  /**
   * Gets a list of all attached devices for mydata display reasons
   */
  public getSingleDevices(): Observable<any>  {
    return this.http.get<{ any }>(environment.apiUrl + 'licence/listsingledevices');
  }

  /**
   * Gets a list of all attached devices for mydata display reasons
   * V2 version gathers information of licenceDate via deviceMgr
   */
  public getSingleDevicesV2(): Observable<any>  {
    return this.http.get<{ any }>(environment.apiUrl + 'licence/v2/listsingledevices');
  }

  /**
   * Gets a list of all attached serial numbers for mydata display reasons
   */
  public getSingleSerials(): Observable<any> {
    return this.http.get<{ any }>(environment.apiUrl + 'licence/listsingleserials');
  }

  public resetLicence(serial: string) {
    return this.http.get<{ any }>(environment.apiUrl + 'licence/deleteuseddevice/' + serial);
  }

  public openLicenceScreen(licence: DeviceLicence, deviceId: string) {
    if (licence.expireDays > 0 && licence.expireDays < 10) {
      if (!this.licenceScreenShownForDevice(deviceId)) {
        this.setLicenceScreenShownForDevice(deviceId);
        this.router.navigate(['/licence']);
      }
    }
    if (licence.expireDays <= 0) {
      this.router.navigate(['/licence']);
    }
  }

  private getLicenceScreenShown(): LicenceScreenShown {
    return JSON.parse(sessionStorage.getItem('licenceScreenShown')) as LicenceScreenShown ?? {};
  }

  private setLicenceScreenShown(value: LicenceScreenShown) {
    sessionStorage.setItem('licenceScreenShown', JSON.stringify(value));
  }

  private licenceScreenShownForDevice(deviceId: string): boolean {
    const licenceScreenShown = this.getLicenceScreenShown();
    return licenceScreenShown[deviceId];
  }

  private setLicenceScreenShownForDevice(deviceId: string, shown = true) {
    const licenceScreenShown = this.getLicenceScreenShown();
    licenceScreenShown[deviceId] = shown;
    this.setLicenceScreenShown(licenceScreenShown);
  }
}


export interface LoginEmail {
  /** Main account of the customer — with a CoAccount login this is NOT the logged in user */
  email?: string;
  /**
   * Address this session logged in with. Empty string for token logins from app/device which carry
   * no identity — the API rejects a password change for those. Undefined only on an older backend
   * which does not know the field yet.
   */
  loginEmail?: string;
  /** Session logged in with an additional login (CoAccount) of another customer */
  isCoAccount?: boolean;
}

/**
 * Whether this session may change its password (mirrors the rule of the API endpoint action/pwchange
 * plus the portal decision to keep CoAccounts out of it entirely).
 *
 * A session without a login identity would have hit the main account, so the API denies it. An older
 * backend does not send `loginEmail` at all — in that case nothing is blocked.
 */
export function canChangePassword(info: LoginEmail | undefined): boolean {
  if (info?.isCoAccount === true) return false;
  if (info?.loginEmail === undefined) return true;
  return info.loginEmail !== '';
}

export interface SingleSerial {
  deviceCount?: string;
  serial?: string;
  validUntil?: string;
}

export interface SingleDevice {
  deviceName?: string;
  deviceType?: number;
  lastDays?: number;
  lastSync?: string;
  lastUser?: string;
  licenceDate?: string;
  licenceIssue: boolean;
  pcId?: string;
  serial?: string;
  remainingDays?: number;
}

export type LicenceScreenShown = {[deviceId: string]: boolean};

